import React, { useCallback, useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components/macro";
import { LeftPanel } from "./left_panel";
import { RightPanel } from "./right_panel";
import { getRequest, getDevToken, isDevMode } from "../utils/commHelper";
import {
  connectPrintersSocket,
  disconnectPrintersSocket,
  onPrintersSocketEvent,
} from "./printerSocket";
import {
  mapJobFromServer,
  printersListToMap,
  upsertJobInList,
  upsertPrinterInMap,
} from "./printerMappers";
import { theme } from "./theme";
import { ToastProvider } from "./toast";

export const sectionList = {
  PRINT_JOBS: "printJob",
  PRINTERS: "printers",
  PRINT_FILE: "printFile",
};

const Printer = () => {
  const [section, setSection] = useState(sectionList.PRINT_JOBS);
  const [jobs, setJobs] = useState([]);
  const [printers, setPrinters] = useState({});
  // Cross-section navigation: clicking a room name in the jobs list jumps to that
  // printer's page. PrinterListPage consumes this once and clears it, so its own
  // back-arrow / list selection keep working off its local state afterwards.
  const [printerToOpen, setPrinterToOpen] = useState(null);
  const [isDev, setIsDev] = useState(false);
  const [canAccessDev, setCanAccessDev] = useState(false);
  const [userFullName, setUserFullName] = useState(null);
  // True only until the very first /dashboardActiveJobs + /dashboardPrinterStatuses
  // snapshot resolves — later socket-driven or reconnect refetches don't touch this,
  // so the initial loader never reappears once the dashboard has loaded once.
  const [initialLoading, setInitialLoading] = useState(true);

  const loadSnapshot = useCallback(async (useDev) => {
    try {
      const [jobsRes, printersRes] = await Promise.all([
        getRequest("/dashboardActiveJobs", useDev),
        getRequest("/dashboardPrinterStatuses", useDev),
      ]);

      const jobsPayload = jobsRes?.data?.result ?? jobsRes?.data ?? [];
      const printersPayload =
        printersRes?.data?.result ?? printersRes?.data ?? [];

      const jobsList = Array.isArray(jobsPayload)
        ? jobsPayload.map(mapJobFromServer).filter(Boolean)
        : [];
      jobsList.sort(
        (a, b) => (b.unix?.getTime?.() || 0) - (a.unix?.getTime?.() || 0)
      );
      setJobs(jobsList);
      setPrinters(
        printersListToMap(
          Array.isArray(printersPayload) ? printersPayload : []
        )
      );
    } catch (e) {
      console.error("[Printer] snapshot load failed", e);
    } finally {
      setInitialLoading(false);
    }
  }, []);

  const loadPermissions = useCallback(async (useDev) => {
    try {
      const res = await getRequest("/getUserPermissions", useDev);
      const data = res?.data?.result ?? res?.data ?? {};
      setCanAccessDev(!!data.canAccessDev);
      setUserFullName(data.fullName || null);
      if (!data.canAccessDev) {
        setIsDev(false);
      }
    } catch (e) {
      setCanAccessDev(false);
      setIsDev(false);
    }
  }, []);

  useEffect(() => {
    let unsubs = [];
    let cancelled = false;

    const detach = () => {
      unsubs.forEach((fn) => fn && fn());
      unsubs = [];
      disconnectPrintersSocket();
    };

    const bootstrap = async () => {
      detach();
      // Dev-token login bypasses Firebase Auth entirely — do not call getAuth.
      if (isDevMode() && !getDevToken()) return;

      await loadPermissions(isDev);
      if (cancelled) return;
      await loadSnapshot(isDev);
      if (cancelled) return;

      const sock = await connectPrintersSocket(isDev);
      if (!sock || cancelled) return;

      unsubs.push(
        onPrintersSocketEvent("printJobsStart", (payload) => {
          setJobs((prev) => upsertJobInList(prev, payload));
        })
      );
      unsubs.push(
        onPrintersSocketEvent("printJobsStatus", (payload) => {
          setJobs((prev) => upsertJobInList(prev, payload));
        })
      );
      unsubs.push(
        onPrintersSocketEvent("printerStatus", (payload) => {
          setPrinters((prev) => upsertPrinterInMap(prev, payload));
        })
      );
      unsubs.push(
        onPrintersSocketEvent("connect", () => {
          // socket.io's own `reconnection: true` silently reconnects after a network
          // blip / server restart / backgrounded tab — but any broadcasts that fired
          // while disconnected are gone forever, no buffering. Refetch the snapshot on
          // every (re)connect so the list catches back up instead of quietly going stale.
          // (Token expiry across a long absence is handled separately, inside
          // printerSocket.js's own "expiredToken" handler — reconnecting there
          // re-fires this same "connect" listener once a fresh token is accepted.)
          loadSnapshot(isDev).catch(() => {});
        })
      );
    };

    bootstrap();

    return () => {
      cancelled = true;
      detach();
    };
  }, [isDev, loadPermissions, loadSnapshot]);

  return (
    <ToastProvider>
      <Wrapper>
        <FontStyle />
        <LeftPanel
          printers={printers}
          jobs={jobs}
          section={section}
          onSectionChange={(val) => setSection(val)}
          isDev={isDev}
          canAccessDev={canAccessDev}
          onToggleDev={(next) => setIsDev(!!next)}
          userFullName={userFullName}
        />
        <RightPanel
          jobs={jobs}
          setJobs={setJobs}
          printers={printers}
          section={section}
          isDev={isDev}
          loading={initialLoading}
          printerToOpen={printerToOpen}
          onOpenPrinter={(printer) => {
            if (!printer?.printer_id) return;
            setPrinterToOpen(printer);
            setSection(sectionList.PRINTERS);
          }}
          onPrinterToOpenConsumed={() => setPrinterToOpen(null)}
        />
      </Wrapper>
    </ToastProvider>
  );
};

// Scoped to this tree only (not the whole app's public/index.html) — loaded via a
// styled-components global style so it mounts/unmounts with the Printer component.
const FontStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 100vh;
  /* LeftPanel is position:fixed (240px wide) and out of normal flow — reserve the
     same space here so RightPanel's content doesn't render underneath it. */
  padding-left: 240px;
  background: ${theme.background};
  font-family: ${theme.fontFamily};
  color: ${theme.text};
`;

export { Printer };
